/**
 * GlobalView — Space Layer Renderer (§4.1)
 *
 * Renders satellite entities on the CesiumJS globe.
 * Uses Cesium.PointPrimitiveCollection for efficient rendering of
 * thousands of satellites at their correct orbital altitude.
 *
 * **Smooth motion**: Client-side SGP4 propagation via satellite.js.
 * TLE data (satrec) is received from the server via entity properties.
 * Every render frame, all visible satellites are re-propagated to
 * the current time → continuous, mathematically exact orbital motion.
 *
 * Features:
 *   - Per-frame SGP4 propagation for smooth orbital motion
 *   - Glowing point primitives at orbital altitude
 *   - Labels with satellite name
 *   - Color coding: gold (general), green (Starlink), red (stations), purple (GEO)
 *   - Visibility controlled by globeStore spaceLayerVisible / starlinkVisible
 */

import * as Cesium from 'cesium';
import * as satellite from 'satellite.js';
import type { Entity, SatelliteProperties } from '@globalview/shared';
import { useEntityStore, selectSatellites } from '../../stores/entityStore';
import { useGlobeStore } from '../../stores/globeStore';
import { getSatelliteIconCanvas, getSatelliteIconType } from './satelliteIcons';

// ─── Types ───────────────────────────────────────────────────────

/** Per-satellite state including parsed satrec for client-side propagation */
interface SatRecord {
  id: string;
  name: string;
  satrec: satellite.SatRec | null;
  color: Cesium.Color;
  pointSize: number;
  isStarlink: boolean;
}

interface SpaceLayerState {
  viewer: Cesium.Viewer;
  billboardCollection: Cesium.BillboardCollection;
  labelCollection: Cesium.LabelCollection;
  billboardMap: Map<string, Cesium.Billboard>;
  labelMap: Map<string, Cesium.Label>;
  satRecords: Map<string, SatRecord>;
  unsubscribe: (() => void) | null;
  unsubscribeVisibility: (() => void) | null;
  unsubscribeSelection: (() => void) | null;
  tickRemove: Cesium.Event.RemoveCallback | null;
  orbitEntity: Cesium.Entity | null;
  orbitSatId: string | null;
}

// ─── Config ──────────────────────────────────────────────────────

const LABEL_FONT = '11px Inter, Helvetica, sans-serif';
const LABEL_OFFSET = new Cesium.Cartesian2(18, -4);
const PROPAGATION_THROTTLE_MS = 200;
const ORBIT_POINTS = 360;
const ORBIT_LINE_COLOR = Cesium.Color.fromCssColorString('#FFD700').withAlpha(0.6);
const ORBIT_LINE_WIDTH = 1.5;
const BILLBOARD_SCALE = 1.0;

// ─── Module State ────────────────────────────────────────────────

let state: SpaceLayerState | null = null;
let lastPropagationTime = 0;

// ─── Helpers ─────────────────────────────────────────────────────

function satColorHex(entity: Entity): string {
  return entity.style.color || '#FFD700';
}

/**
 * Parse a satrec from TLE lines stored in entity properties.
 * This is called once per satellite when first seen.
 */
function parseSatrec(props: SatelliteProperties | undefined): satellite.SatRec | null {
  if (!props?.tleLine1 || !props?.tleLine2) return null;
  try {
    return satellite.twoline2satrec(props.tleLine1, props.tleLine2);
  } catch {
    return null;
  }
}

// ─── Entity Catalog Sync ─────────────────────────────────────────

/**
 * Sync satellite entities from the store into our local catalog.
 * This builds/updates the SatRecord map (with parsed satrec) and
 * creates/removes Cesium primitives.
 */
function syncCatalog(satellites: Entity[]): void {
  if (!state) return;

  const { billboardCollection, labelCollection, billboardMap, labelMap, satRecords } = state;
  const starlinkVisible = useGlobeStore.getState().starlinkVisible;
  const labelsVisible = useGlobeStore.getState().spaceLabelsVisible;

  const activeIds = new Set<string>();

  for (const entity of satellites) {
    const props = entity.properties as unknown as SatelliteProperties;
    const isStarlink = props?.constellation === 'Starlink';

    if (isStarlink && !starlinkVisible) continue;

    activeIds.add(entity.id);

    let record = satRecords.get(entity.id);
    if (!record) {
      record = {
        id: entity.id,
        name: props?.name ?? entity.id,
        satrec: parseSatrec(props),
        color: Cesium.Color.WHITE, // not used for billboards
        pointSize: 0,
        isStarlink,
      };
      satRecords.set(entity.id, record);
    }

    // Create billboard if it doesn't exist
    if (!billboardMap.has(entity.id)) {
      const position = Cesium.Cartesian3.fromDegrees(
        entity.position.lon,
        entity.position.lat,
        entity.position.alt,
      );
      const colorHex = satColorHex(entity);
      const iconType = getSatelliteIconType(record.name, props?.constellation);
      const canvas = getSatelliteIconCanvas(iconType, colorHex);

      const billboard = billboardCollection.add({
        position,
        image: canvas,
        scale: BILLBOARD_SCALE,
        id: entity.id,
        scaleByDistance: new Cesium.NearFarScalar(1e6, 1.5, 1e8, 0.4),
        translucencyByDistance: new Cesium.NearFarScalar(1e6, 1.0, 1e8, 0.6),
      });
      billboardMap.set(entity.id, billboard);
    }

    // Create label if it doesn't exist
    if (!labelMap.has(entity.id)) {
      const position = Cesium.Cartesian3.fromDegrees(
        entity.position.lon,
        entity.position.lat,
        entity.position.alt,
      );
      const label = labelCollection.add({
        position,
        text: record.name,
        font: LABEL_FONT,
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 2,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        pixelOffset: LABEL_OFFSET,
        show: labelsVisible,
        id: entity.id,
        scaleByDistance: new Cesium.NearFarScalar(1e6, 1.0, 1e8, 0.3),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 5e7),
      });
      labelMap.set(entity.id, label);
    }
  }

  // Remove stale entries
  for (const [id, billboard] of billboardMap) {
    if (!activeIds.has(id)) {
      billboardCollection.remove(billboard);
      billboardMap.delete(id);
      satRecords.delete(id);
    }
  }
  for (const [id, label] of labelMap) {
    if (!activeIds.has(id)) {
      labelCollection.remove(label);
      labelMap.delete(id);
    }
  }
}

// ─── Per-Frame SGP4 Propagation ──────────────────────────────────

/**
 * Called on each Cesium render tick.
 * Propagates all visible satellites to current time using SGP4.
 */
function onTick(): void {
  if (!state) return;
  if (!useGlobeStore.getState().spaceLayerVisible) return;

  // Throttle: don't propagate every single frame
  const now = performance.now();
  if (now - lastPropagationTime < PROPAGATION_THROTTLE_MS) return;
  lastPropagationTime = now;

  const { billboardMap, labelMap, satRecords } = state;
  const currentTime = new Date();
  const gmst = satellite.gstime(currentTime);

  for (const [id, record] of satRecords) {
    if (!record.satrec) continue;

    try {
      const positionAndVelocity = satellite.propagate(record.satrec, currentTime);
      if (!positionAndVelocity) continue;
      const positionEci = positionAndVelocity.position;
      if (!positionEci || typeof positionEci === 'boolean') continue;

      const positionGd = satellite.eciToGeodetic(positionEci, gmst);
      const lat = satellite.degreesLat(positionGd.latitude);
      const lon = satellite.degreesLong(positionGd.longitude);
      const altM = positionGd.height * 1000;

      if (isNaN(lat) || isNaN(lon) || isNaN(altM)) continue;
      if (positionGd.height < 0 || positionGd.height > 100_000) continue;

      const cartesian = Cesium.Cartesian3.fromDegrees(lon, lat, altM);

      // Update billboard position
      const billboard = billboardMap.get(id);
      if (billboard) {
        billboard.position = cartesian;
      }

      // Update label position
      const label = labelMap.get(id);
      if (label) {
        label.position = cartesian;
      }
    } catch {
      // SGP4 can fail for decayed orbits — skip silently
      continue;
    }
  }
}

// ─── Orbit Line Computation ────────────────────────────────────

/**
 * Compute the full orbit trajectory for a satellite.
 * Propagates SGP4 for one full orbital period, producing ~ORBIT_POINTS positions.
 */
function computeOrbitPositions(satrec: satellite.SatRec, periodMinutes: number): Cesium.Cartesian3[] {
  const positions: Cesium.Cartesian3[] = [];
  const now = new Date();
  // Use at least 90 minutes if period is unknown
  const periodMs = (periodMinutes > 0 ? periodMinutes : 90) * 60 * 1000;
  const stepMs = periodMs / ORBIT_POINTS;

  for (let i = 0; i <= ORBIT_POINTS; i++) {
    const time = new Date(now.getTime() + i * stepMs);
    try {
      const gmst = satellite.gstime(time);
      const pv = satellite.propagate(satrec, time);
      if (!pv) continue;
      const posEci = pv.position;
      if (!posEci || typeof posEci === 'boolean') continue;

      const gd = satellite.eciToGeodetic(posEci, gmst);
      const lat = satellite.degreesLat(gd.latitude);
      const lon = satellite.degreesLong(gd.longitude);
      const altM = gd.height * 1000;

      if (isNaN(lat) || isNaN(lon) || isNaN(altM)) continue;
      if (gd.height < 0 || gd.height > 100_000) continue;

      positions.push(Cesium.Cartesian3.fromDegrees(lon, lat, altM));
    } catch {
      continue;
    }
  }

  return positions;
}

/**
 * Show or update orbit line for a selected satellite.
 */
function showOrbitLine(satId: string): void {
  if (!state) return;

  const record = state.satRecords.get(satId);
  if (!record?.satrec) return;

  // Get period from entity properties
  const entity = useEntityStore.getState().entities.get(satId);
  const props = entity?.properties as unknown as SatelliteProperties | undefined;
  const period = props?.period ?? 0;

  const positions = computeOrbitPositions(record.satrec, period);
  if (positions.length < 2) return;

  // Remove previous orbit line if exists
  removeOrbitLine();

  // Create orbit polyline as a Cesium Entity
  state.orbitEntity = state.viewer.entities.add({
    polyline: {
      positions,
      width: ORBIT_LINE_WIDTH,
      material: new Cesium.PolylineGlowMaterialProperty({
        glowPower: 0.15,
        color: ORBIT_LINE_COLOR,
      }),
      clampToGround: false,
    },
  });
  state.orbitSatId = satId;
}

/**
 * Remove the current orbit line.
 */
function removeOrbitLine(): void {
  if (!state) return;
  if (state.orbitEntity) {
    state.viewer.entities.remove(state.orbitEntity);
    state.orbitEntity = null;
    state.orbitSatId = null;
  }
}

// ─── Public API ──────────────────────────────────────────────────

export function initSpaceLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  const billboardCollection = viewer.scene.primitives.add(
    new Cesium.BillboardCollection(),
  ) as Cesium.BillboardCollection;

  const labelCollection = viewer.scene.primitives.add(
    new Cesium.LabelCollection(),
  ) as Cesium.LabelCollection;

  state = {
    viewer,
    billboardCollection,
    labelCollection,
    billboardMap: new Map(),
    labelMap: new Map(),
    satRecords: new Map(),
    unsubscribe: null,
    unsubscribeVisibility: null,
    unsubscribeSelection: null,
    tickRemove: null,
    orbitEntity: null,
    orbitSatId: null,
  };

  // Subscribe to entity store — catalog sync (not position updates)
  state.unsubscribe = useEntityStore.subscribe(() => {
    if (!state) return;
    const visible = useGlobeStore.getState().spaceLayerVisible;
    if (!visible) return;

    const satellites = selectSatellites(useEntityStore.getState());
    syncCatalog(satellites);
  });

  // Subscribe to visibility changes
  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    // Layer visibility toggle
    if (curr.spaceLayerVisible !== prev.spaceLayerVisible) {
      state.billboardCollection.show = curr.spaceLayerVisible;
      state.labelCollection.show = curr.spaceLayerVisible;

      if (curr.spaceLayerVisible) {
        const satellites = selectSatellites(useEntityStore.getState());
        syncCatalog(satellites);
      }
    }

    // Label visibility toggle
    if (curr.spaceLabelsVisible !== prev.spaceLabelsVisible) {
      for (const label of state.labelMap.values()) {
        label.show = curr.spaceLabelsVisible;
      }
    }

    // Starlink toggle — re-sync catalog
    if (curr.starlinkVisible !== prev.starlinkVisible) {
      if (curr.spaceLayerVisible) {
        const satellites = selectSatellites(useEntityStore.getState());
        syncCatalog(satellites);
      }
    }
  });

  // Per-frame propagation via Cesium tick
  state.tickRemove = viewer.scene.postRender.addEventListener(onTick);

  // Subscribe to selection changes — show/hide orbit line
  state.unsubscribeSelection = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;
    if (curr.selectedEntityId === prev.selectedEntityId) return;

    const selId = curr.selectedEntityId;

    if (selId && selId.startsWith('space-') && state.satRecords.has(selId)) {
      showOrbitLine(selId);
    } else {
      removeOrbitLine();
    }
  });

  // Initial visibility
  billboardCollection.show = useGlobeStore.getState().spaceLayerVisible;
  labelCollection.show = useGlobeStore.getState().spaceLayerVisible;

  console.log('[SpaceLayer] Initialized (client-side SGP4 + icons)');
}

export function destroySpaceLayer(): void {
  if (!state) return;

  if (state.unsubscribe) state.unsubscribe();
  if (state.unsubscribeVisibility) state.unsubscribeVisibility();
  if (state.unsubscribeSelection) state.unsubscribeSelection();
  if (state.tickRemove) state.tickRemove();

  removeOrbitLine();

  state.viewer.scene.primitives.remove(state.billboardCollection);
  state.viewer.scene.primitives.remove(state.labelCollection);

  state.billboardMap.clear();
  state.labelMap.clear();
  state.satRecords.clear();

  state = null;
  console.log('[SpaceLayer] Destroyed');
}

/**
 * Get the current Cartesian3 position of a satellite (from its billboard).
 * Used by the satellite tracker to feed Cesium's trackedEntity.
 */
export function getSatellitePosition(satId: string): Cesium.Cartesian3 | null {
  if (!state) return null;
  const billboard = state.billboardMap.get(satId);
  if (!billboard) return null;
  return billboard.position as Cesium.Cartesian3;
}
